import { CloseIcon, CommentAltIcon, EllipsisIcon, EmailIcon, FlagIcon, TrashIcon, UserIcon } from "@xola/icons";
import { flash, Popover } from "@xola/ui-kit";
import { getDemographicCatalog, PurchaseActionOption } from "common";
import { some } from "lodash";
import React, { useMemo } from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import { EmailTemplate, Experience, ExperiencePurchaseItem, Guest, Purchase, Traveler } from "types";
import { api } from "@/api/api";
import { Menu } from "@/components/Common/Menu";
import { MessageDrawer } from "@/components/Common/MessageModal/MessageDrawer";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { RefundOption } from "@/components/Dashboard/RefundOption";
import { PurchaseChangeQuantityModal } from "@/components/PurchaseModification/ChangeQuantity/PurchaseChangeQuantityModal";
import { SplitPayRefundModal } from "@/components/PurchaseModification/SplitPayRefundModal";
import { SplitPayRemovePayerModal } from "@/components/PurchaseModification/SplitPayRemovePayerModal";
import { usePostCheckinWarningModal } from "@/hooks/usePostCheckinWarningModal";
import { useResource } from "@/hooks/useResource";
import { useVisibility } from "@/hooks/useVisibility";
import { getGuestEmail, getGuestPhone } from "@/models/Guest";
import { getAmountForTraveler, getTravelerPaymentFormItemCount } from "@/models/Purchase";
import { isAuthorizeNetEnabled } from "@/models/Seller";
import { useCreatePurchaseStore } from "@/stores/createPurchaseStore";
import { getSeller } from "@/utils/getSeller";

interface SplitPayGuestMenuProps {
    purchase: Purchase;
    purchaseItem: ExperiencePurchaseItem;
    traveler?: Traveler;
    guest?: Guest;
    onOptionsChange?: (
        modalOption: PurchaseActionOption | null,
        refundOption: RefundOption | null,
        refundAmount: number,
    ) => void;
    onFilterGuests?: (isRemoveGuestOpen: boolean, guest: Guest) => Guest[];
}

export const SplitPayGuestMenu = ({
    purchase,
    purchaseItem,
    traveler,
    guest,
    onOptionsChange,
    onFilterGuests,
}: SplitPayGuestMenuProps) => {
    const history = useHistory();
    const [isMenuOpen, { show: showMenu, hide: hideMenu }] = useVisibility(false);
    const [isMessageModalOpen, { show: showMessageModal, hide: hideMessageModal }] = useVisibility(false);
    const [isChangeDemographicModalOpen, { show: showChangeDemographicModal, hide: hideChangeDemographicModal }] =
        useVisibility(false);
    const [isRemoveGuestModalOpen, { show: showRemoveGuestModal, hide: hideRemoveGuestModal }] = useVisibility(false);
    const [isRemoveFromBookingModalOpen, { show: showRemoveFromBookingModal, hide: hideRemoveFromBookingModal }] =
        useVisibility(false);
    const [isMultiPaymentRefundModalOpen, { show: showMultiPaymentRefundModal, hide: hideMultiPaymentRefundModal }] =
        useVisibility(false);
    const { PostCheckInWarningModal, withPostCheckInWarning } = usePostCheckinWarningModal(purchaseItem);

    const hasMultiplePayments = useMemo(() => {
        if (!traveler?.id) return false;
        const seller = getSeller();
        return getTravelerPaymentFormItemCount(purchase, traveler.id, isAuthorizeNetEnabled(seller)) > 1;
    }, [purchase, traveler?.id]);

    const { data: experience } = useResource<Experience>(["products", purchaseItem.product.id]);

    const userId = traveler?.id;

    const userEmail = traveler?.email || (guest && getGuestEmail(guest));

    const { mutateAsync: sendSplitPaymentEmail } = useMutation(async () => {
        return api.post(`/conversations/${purchase.conversation.id}/messages`, {
            meta: { template: EmailTemplate.SPLIT_PAY_INVITE },
            to: { id: userId },
        });
    });

    const { mutateAsync: sendConfirmationEmail } = useMutation(async () => {
        return api.post(`/conversations/${purchase.conversation.id}/messages`, {
            meta: { template: EmailTemplate.PURCHASE_CONFIRMATION },
            to: { id: userId },
        });
    });

    const handleResendInvite = async () => {
        sendConfirmationEmail();
        flash.show({ text: `Email confirmation re-sent to ${traveler?.email}`, color: "success" });
    };

    const handleResendSplitInvite = async () => {
        sendSplitPaymentEmail();
        flash.show({ text: `Email confirmation re-sent to ${traveler?.email}`, color: "success" });
    };

    const showMessage = traveler ?? (guest ? getGuestEmail(guest) ?? getGuestPhone(guest) : false);
    const showRemoveGuest = guest && purchaseItem.guests.length > 1;
    const demographics = experience && getDemographicCatalog(experience);
    const showChangeDemographic = guest && demographics && demographics.length > 1;
    const showMenus = some([userEmail, showMessage, showChangeDemographic, traveler, showRemoveGuest], Boolean);

    const showRemoveFromBooking = !!(
        traveler &&
        purchase?.organizer?.id !== traveler?.id &&
        Math.abs(getAmountForTraveler(purchase, traveler?.id)) > 0
    );

    return (
        <>
            {showMenus && (
                <Popover visible={isMenuOpen} maxWidth={300} placement="bottom" zIndex="25" onClickOutside={hideMenu}>
                    <SecondaryButton
                        data-testid="split-pay-guest-menu-button"
                        size="tiny"
                        className="!inline"
                        onClick={showMenu}
                    >
                        <EllipsisIcon />
                    </SecondaryButton>
                    <Popover.Content>
                        <Menu>
                            {userEmail && (
                                <Menu.Item
                                    icon={<EmailIcon />}
                                    onClick={() => {
                                        navigator.clipboard.writeText(userEmail);
                                        flash.success("Copied to Clipboard");
                                    }}
                                >
                                    Copy Email <span className="pl-1 font-thin">({userEmail})</span>
                                </Menu.Item>
                            )}
                            {showMessage && (
                                <Menu.Item icon={<CommentAltIcon />} onClick={showMessageModal}>
                                    Message
                                </Menu.Item>
                            )}
                            {showChangeDemographic && (
                                <Menu.Item
                                    icon={<UserIcon />}
                                    onClick={withPostCheckInWarning(() => {
                                        showChangeDemographicModal();
                                        hideMenu();
                                    })}
                                >
                                    Change Demographic
                                </Menu.Item>
                            )}
                            {traveler && (
                                <>
                                    <Menu.Item icon={<EmailIcon />} onClick={async () => handleResendInvite()}>
                                        Resend Confirmation E-mail
                                    </Menu.Item>
                                    {purchase.splitPayment.enabled && (
                                        <Menu.Item icon={<EmailIcon />} onClick={async () => handleResendSplitInvite()}>
                                            Send Split-Pay E-mail
                                        </Menu.Item>
                                    )}
                                    <Menu.Item
                                        icon={<FlagIcon />}
                                        onClick={withPostCheckInWarning(() => {
                                            useCreatePurchaseStore.setState({ organizer: traveler });
                                            history.push("/cart/init");
                                        })}
                                    >
                                        Rebook Customer
                                    </Menu.Item>
                                </>
                            )}
                            {showRemoveGuest && (
                                <Menu.Item icon={<TrashIcon />} onClick={withPostCheckInWarning(showRemoveGuestModal)}>
                                    Remove Guest
                                </Menu.Item>
                            )}
                            {showRemoveFromBooking && (
                                <Menu.Item
                                    icon={<CloseIcon />}
                                    onClick={withPostCheckInWarning(
                                        hasMultiplePayments ? showMultiPaymentRefundModal : showRemoveFromBookingModal,
                                    )}
                                >
                                    Remove from Booking
                                </Menu.Item>
                            )}
                        </Menu>
                    </Popover.Content>
                </Popover>
            )}
            <MessageDrawer
                filters={{ purchase: { id: purchase.id }, item: purchaseItem?.id }}
                to={[{ id: traveler?.id, type: "traveler" }]}
                isOpen={isMessageModalOpen}
                channels={{ isPhoneAvailable: !!traveler?.phone }}
                description={
                    <>
                        This message will only be sent to attendee <var data-var="attendeeName">{traveler?.name}</var>
                    </>
                }
                onClose={hideMessageModal}
            />

            {onFilterGuests && onOptionsChange && (
                <PurchaseChangeQuantityModal
                    attendee={{ isChangeDemographic: isChangeDemographicModalOpen, removedAttendee: guest?.id }}
                    purchase={purchase}
                    item={purchaseItem}
                    guests={onFilterGuests(isRemoveGuestModalOpen, guest!)}
                    isOpen={isChangeDemographicModalOpen || isRemoveGuestModalOpen}
                    onClose={(
                        nextModalOption: PurchaseActionOption | null,
                        nextRefundOption: RefundOption | null,
                        refundAmount,
                    ) => {
                        hideChangeDemographicModal();
                        hideRemoveGuestModal();
                        onOptionsChange(nextModalOption, nextRefundOption, refundAmount);
                    }}
                />
            )}

            <SplitPayRemovePayerModal
                isOpen={isRemoveFromBookingModalOpen}
                purchase={purchase}
                item={purchaseItem}
                traveler={traveler}
                onClose={hideRemoveFromBookingModal}
            />

            <SplitPayRefundModal
                isOpen={isMultiPaymentRefundModalOpen}
                purchase={purchase}
                item={purchaseItem}
                shouldReduce={false}
                filterTravelerId={traveler?.id}
                onClose={hideMultiPaymentRefundModal}
            />

            <PostCheckInWarningModal />
        </>
    );
};
